import "core-js/features/array/at";
import { ApolloProvider } from "@apollo/client";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import Head from "next/head";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import React from "react";
import type { ReactElement, ReactNode } from "react";

import { GtmProvider } from "src/analytics/GtmProvider";
import { RedirectNewUrl } from "src/hooks/RedirectNewUrl";
import { CurrentVisitProvider } from "src/hooks/useCurrentVisitNotification";
import { HistoryProvider } from "src/hooks/useHistory";
import { LoadingOverlayProvider } from "src/hooks/useLoading";
import { SideMenuOpenProvider } from "src/hooks/useSideMenuOpen";
import { initializeDatadogRum } from "src/lib/monitoring/datadog";
import { MomoChakraProvider } from "src/providers/MomoChakraProvider";
import { PWADeviceProvider } from "src/providers/PWADeviceProvider";
import { ReceptionTimeSlotsProvider } from "src/providers/ReceptionTimeSlotsProvider";
import { SentryProvider } from "src/providers/SentryProvider";
import { ServiceWorkerProvider } from "src/providers/ServiceWorkerProvider";
import { useApollo } from "src/store/useApollo";

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

initializeDatadogRum();

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? getCommonLayout;

  return getLayout(<Component {...pageProps} />);
};

// NOTE: getLayout を直接呼び出している箇所の対応も忘れずに行う
const getCommonLayout = (page: ReactElement) => {
  const apolloClient = useApollo();

  return (
    <>
      <Head>
        <title>クロンスマートパス</title>
      </Head>

      <RedirectNewUrl>
        <ApolloProvider client={apolloClient}>
          <NuqsAdapter>
            <SentryProvider>
              <GtmProvider>
                <MomoChakraProvider>
                  <LoadingOverlayProvider>
                    <SideMenuOpenProvider>
                      <HistoryProvider>
                        <ServiceWorkerProvider>
                          <PWADeviceProvider>
                            <CurrentVisitProvider>
                              <ReceptionTimeSlotsProvider>{page}</ReceptionTimeSlotsProvider>
                            </CurrentVisitProvider>
                          </PWADeviceProvider>
                        </ServiceWorkerProvider>
                      </HistoryProvider>
                    </SideMenuOpenProvider>
                  </LoadingOverlayProvider>
                </MomoChakraProvider>
              </GtmProvider>
            </SentryProvider>
          </NuqsAdapter>
        </ApolloProvider>
      </RedirectNewUrl>
    </>
  );
};

export default App;
